import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';

const DISMISS_KEY = 'jnd:ios-install-dismissed';
const COOLDOWN_DAYS = 30;

function isIOSSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const iOS = /iPhone|iPad|iPod/.test(ua);
  if (!iOS) return false;
  // Detect Safari (not Chrome/Firefox iOS, which all use WebKit but report different UAs)
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true;
}

function loadDismissedAt(): number {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    return v ? Number(v) : 0;
  } catch {
    return 0;
  }
}

function setDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function InstallPromptIOS() {
  const [show, setShow] = useState(false);
  const prefs = useLiveQuery(() => db.prefs.get('singleton'), [], undefined);

  useEffect(() => {
    if (!isIOSSafari() || isStandalone()) return;
    const dismissedAt = loadDismissedAt();
    const cooldown = COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
    if (Date.now() - dismissedAt < cooldown) return;
    if (prefs?.installedDismissedAt && Date.now() - prefs.installedDismissedAt < cooldown) return;
    // Show after a short delay so it doesn't punch in over the first paint.
    const handle = setTimeout(() => setShow(true), 1800);
    return () => clearTimeout(handle);
  }, [prefs?.installedDismissedAt]);

  if (!show) return null;

  const dismiss = async () => {
    setShow(false);
    setDismissed();
    if (prefs) {
      await db.prefs.put({ ...prefs, installedDismissedAt: Date.now() });
    }
  };

  return (
    <div
      className="install-banner"
      role="dialog"
      aria-labelledby="ios-install-title"
      aria-describedby="ios-install-body"
    >
      <h2 id="ios-install-title" style={{ marginTop: 0, fontSize: '1rem' }}>
        Add JND Bible to your Home Screen
      </h2>
      <p id="ios-install-body" className="muted" style={{ margin: '.5rem 0', fontSize: '.85rem' }}>
        Tap <strong>Share</strong>{' '}
        <span aria-hidden style={{ display: 'inline-block', verticalAlign: 'middle' }}>⎘</span>{' '}
        then <strong>Add to Home Screen</strong>{' '}
        <span aria-hidden style={{ display: 'inline-block', verticalAlign: 'middle' }}>＋</span>
        . Opening from the icon enables offline reading and (later) daily-verse notifications.
      </p>
      <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
        <button className="btn-secondary" onClick={dismiss}>
          Not now
        </button>
      </div>
    </div>
  );
}
